import { useMemo, useState } from 'react';
import { ListMusic, Loader2, Search, Wrench, X } from 'lucide-react';
import type { SongRequest } from '../../services/requests';
import type { PlayModeConfig } from '../../services/playModes';
import { WrongSongPanel } from './WrongSongPanel';
import {
  defaultPreviousFate,
  previousFateOptions,
  type DetectedTrack,
  type PreviousFate,
  type RecoveryEntry,
  type RecoveryOutcome,
} from '../../utils/nowPlayingRecovery';
import { likelyMatches } from '../../utils/likelyMatches';

type Step = 'confirm' | 'whatInstead' | 'pick' | 'fate' | 'changeLink';
type Pending = { kind: 'pick'; requestId: string } | { kind: 'notInQueue' } | { kind: 'letItPlay' };

export interface FixNowPlayingDialogProps {
  entry: RecoveryEntry;
  nowPlaying: SongRequest | null;
  queue: SongRequest[];
  playMode: PlayModeConfig;
  detected: DetectedTrack | null;
  canFlag: boolean;
  onOutcome: (outcome: RecoveryOutcome) => Promise<void>;
  onClose: () => void;
}

function PickRow({ request, position, disabled, onPick }: {
  request: SongRequest;
  position: number;
  disabled: boolean;
  onPick: (requestId: string) => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onPick(request.id)}
      className="w-full flex items-center gap-3 py-2 px-1 text-left hover:bg-gray-50 dark:hover:bg-gray-800 rounded"
    >
      <span className="w-6 text-xs text-gray-400 tabular-nums">#{position}</span>
      <ListMusic className="w-4 h-4 text-gray-400 flex-shrink-0" />
      <span className="flex-1 min-w-0">
        <span className="block truncate font-medium text-gray-900 dark:text-white">{request.song.title}</span>
        <span className="block truncate text-xs text-gray-500 dark:text-gray-400">{request.song.artist}</span>
      </span>
    </button>
  );
}

function firstStep(entry: RecoveryEntry, nowPlaying: SongRequest | null): Step {
  if (entry === 'fix') return 'confirm';
  if (entry === 'choose') return 'pick';
  return nowPlaying ? 'fate' : 'pick';
}

export function FixNowPlayingDialog({
  entry,
  nowPlaying,
  queue,
  playMode,
  detected,
  canFlag,
  onOutcome,
  onClose,
}: FixNowPlayingDialogProps) {
  const [step, setStep] = useState<Step>(() => firstStep(entry, nowPlaying));
  const [pending, setPending] = useState<Pending | null>(entry === 'letItPlay' ? { kind: 'letItPlay' } : null);
  const [filter, setFilter] = useState('');
  const [busy, setBusy] = useState(false);

  const fateOptions = previousFateOptions(nowPlaying);
  const suggestedFate = defaultPreviousFate(entry);
  const canChangeLink = Boolean(nowPlaying && !nowPlaying.localFile && !nowPlaying.unplannedPlayAt);

  const pickable = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    return queue
      .map((request, index) => ({ request, position: index + 1 }))
      .filter(({ request }) => !request.localFile && request.id !== nowPlaying?.id)
      .filter(({ request }) => !needle
        || request.song.title.toLowerCase().includes(needle)
        || request.song.artist.toLowerCase().includes(needle));
  }, [queue, filter, nowPlaying?.id]);

  // Loose guesses from what Bridge heard (ITER-181); hidden while the DJ searches
  const suggestions = useMemo(
    () => (filter.trim() ? [] : likelyMatches(detected, pickable)),
    [filter, detected, pickable],
  );

  const finish = async (next: Pending, previousTo: PreviousFate) => {
    let outcome: RecoveryOutcome;
    if (next.kind === 'pick') {
      outcome = { kind: 'pickFromQueue', requestId: next.requestId, previousTo, flagPicked: canFlag };
    } else if (detected) {
      outcome = { kind: 'letItPlay', track: detected, previousTo };
    } else {
      outcome = { kind: 'clear', previousTo };
    }
    setBusy(true);
    try {
      await onOutcome(outcome);
    } finally {
      setBusy(false);
    }
  };

  const goToFate = (next: Pending) => {
    setPending(next);
    if (!nowPlaying) {
      void finish(next, 'played');
      return;
    }
    setStep('fate');
  };

  const pickRequest = (requestId: string) => goToFate({ kind: 'pick', requestId });

  const title = entry === 'fix'
    ? 'Fix Now Playing'
    : entry === 'choose'
      ? 'Choose from queue'
      : detected ? `Let “${detected.title}” finish` : 'Let it finish';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="fix-now-playing-dialog">
      <div className="bg-white dark:bg-gray-900 rounded-xl max-w-lg w-full p-6 shadow-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
              <Wrench className="w-5 h-5 text-primary" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h3>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {step === 'confirm' && nowPlaying && (
            <div className="space-y-4">
              <p className="text-gray-700 dark:text-gray-300">
                Is <span className="font-semibold">{nowPlaying.song.title}</span> what&rsquo;s playing in your player right now?
              </p>
              <div className="flex gap-3">
                <button type="button" onClick={onClose} className="btn btn-primary flex-1">Yes</button>
                <button type="button" onClick={() => setStep('whatInstead')} className="btn btn-secondary flex-1">No</button>
              </div>
            </div>
          )}

          {step === 'whatInstead' && (
            <div className="space-y-2">
              <p className="text-gray-700 dark:text-gray-300 mb-3">What&rsquo;s playing instead?</p>
              <button type="button" onClick={() => setStep('pick')} className="btn btn-secondary w-full justify-start text-left">
                Another song from the queue
              </button>
              {canChangeLink && (
                <button type="button" onClick={() => setStep('changeLink')} className="btn btn-secondary w-full justify-start text-left">
                  A different version of this song (the link opened the wrong track)
                </button>
              )}
              <button type="button" onClick={() => goToFate({ kind: 'notInQueue' })} className="btn btn-secondary w-full justify-start text-left">
                Something that&rsquo;s not in the queue
              </button>
            </div>
          )}

          {step === 'pick' && (
            <div className="space-y-3">
              <p className="text-gray-700 dark:text-gray-300">Pick the queue song that&rsquo;s actually playing.</p>
              {detected && (
                <p className="text-sm text-gray-700 dark:text-gray-300" data-testid="pick-heard">
                  Your player is playing: <span className="font-semibold">{detected.title} – {detected.artist}</span>
                </p>
              )}
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={filter}
                  onChange={event => setFilter(event.target.value)}
                  placeholder="Search the queue"
                  className="input w-full pl-9"
                />
              </div>
              {suggestions.length > 0 && (
                <div className="rounded-lg border border-primary/40 bg-primary/5 px-2 py-1" data-testid="likely-matches">
                  <p className="text-xs font-semibold uppercase tracking-wide text-primary px-1 pt-1">Likely match</p>
                  <ul className="divide-y divide-primary/10">
                    {suggestions.map(({ request, position }) => (
                      <li key={request.id}>
                        <PickRow request={request} position={position} disabled={busy} onPick={pickRequest} />
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {pickable.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">No queue songs match.</p>
              ) : (
                <ul className="divide-y divide-gray-100 dark:divide-gray-800">
                  {pickable.map(({ request, position }) => (
                    <li key={request.id}>
                      <PickRow request={request} position={position} disabled={busy} onPick={pickRequest} />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {step === 'fate' && nowPlaying && pending && (
            <div className="space-y-4">
              {pending.kind !== 'pick' && (
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {detected
                    ? <>Now Playing becomes <span className="font-semibold">{detected.title} – {detected.artist}</span>, marked as played unplanned.</>
                    : 'Now Playing will be empty until you start the next song.'}
                </p>
              )}
              <p className="text-gray-700 dark:text-gray-300">
                What about <span className="font-semibold">{nowPlaying.song.title}</span>?
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                {fateOptions.includes('played') && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => finish(pending, 'played')}
                    className={`btn flex-1 ${suggestedFate === 'played' ? 'btn-primary' : 'btn-secondary'}`}
                  >
                    It played
                  </button>
                )}
                {fateOptions.includes('queued') && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => finish(pending, 'queued')}
                    className="btn btn-secondary flex-1"
                  >
                    It didn&rsquo;t play — back to the queue
                  </button>
                )}
              </div>
              {busy && <Loader2 className="w-4 h-4 animate-spin text-primary" />}
            </div>
          )}

          {step === 'changeLink' && nowPlaying && (
            <WrongSongPanel request={nowPlaying} playMode={playMode} mode="report" onDone={onClose} />
          )}
        </div>

      </div>
    </div>
  );
}
